import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon, ViewColumnsIcon } from '@heroicons/react/24/outline';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../contexts/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { isStaff } from '../types/auth';
import { venuesAPI, venueBookingsAPI } from '../services/api';
import { Venue } from '../types/venue';
import { OccupancySlot } from '../types/venueBooking';
import WeeklyOccupancyGrid from '../components/venues/WeeklyOccupancyGrid';
import DailyOccupancyGrid from '../components/venues/DailyOccupancyGrid';
import DayAgenda from '../components/venues/DayAgenda';
import BookingModal from '../components/venues/BookingModal';
import Modal from '../components/ui/Modal';
import PitchGraphic from '../components/venues/PitchGraphic';
import { sectionsLabel } from '../utils/venueSections';
import {
  GroupedSlot,
  SLOT_STYLES,
  SLOT_TYPE_LABELS,
  addDays,
  getMonday,
  groupSlots,
  isoDate,
  localDateTime,
  sameDay,
  slotStyle,
  slotsOnDay,
  timeOf,
} from '../components/venues/occupancy';

/** A magában álló napszám után is kell a pont ("okt. 12. – 18."). */
const withDayDot = (text: string) => (/^\d+$/.test(text) ? `${text}.` : text);

type ViewMode = 'day' | 'week';

const DAY_SHORT = ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'];
const LEGEND = ['Training', 'Match', 'Meeting', 'Foglalás'];

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Pálya foglaltság: melyik pálya, mikor, melyik része foglalt. Egy hétsávval
 * lehet napot választani; napi nézetben asztalon pályarészenkénti idővonal,
 * telefonon időrendi lista a szabad sávokkal. Heti nézetben asztalon hét
 * oszlop, telefonon napokra bontott lista. A stáb innen foglalhat is.
 */
const VenueOccupancy: React.FC = () => {
  usePageTitle('Pálya foglaltság');
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const canBook = isStaff(user);

  const [venues, setVenues] = useState<Venue[]>([]);
  const [venueId, setVenueId] = useState<number | null>(null);
  const [view, setView] = useState<ViewMode>(() => (isMobile ? 'day' : 'week'));
  const [selectedDay, setSelectedDay] = useState<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [slots, setSlots] = useState<OccupancySlot[]>([]);
  const [loadedWeek, setLoadedWeek] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<GroupedSlot | null>(null);
  const [booking, setBooking] = useState<{ start?: string; section?: number | null } | null>(null);

  const weekStart = useMemo(() => getMonday(selectedDay), [selectedDay]);
  const weekKey = isoDate(weekStart);
  const venue = venues.find((v) => v.id === venueId);
  const sectionCount = venue?.sections ?? 1;

  useEffect(() => {
    venuesAPI
      .getAllVenues()
      .then((data) => {
        const active = data.filter((v) => v.isActive);
        setVenues(active);
        if (active.length > 0) setVenueId(active[0].id);
      })
      .catch(() => setError('A pályákat nem sikerült betölteni.'))
      .finally(() => setLoading(false));
  }, []);

  // Mindig az egész hetet töltjük: a hétsáv pöttyei és a napi nézet is ebből él
  const loadWeek = useCallback(async () => {
    if (!venueId) return;
    setError(null);
    try {
      setSlots(await venueBookingsAPI.getWeeklyOccupancy(venueId, weekKey));
      setLoadedWeek(`${venueId}-${weekKey}`);
    } catch {
      setError('A foglaltságot nem sikerült betölteni.');
    }
  }, [venueId, weekKey]);

  useEffect(() => {
    loadWeek();
  }, [loadWeek]);

  const ready = loadedWeek === `${venueId}-${weekKey}`;
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const today = new Date();
  const daySlots = slotsOnDay(slots, selectedDay);
  const dayGroups = groupSlots(daySlots);

  const move = (step: number) => setSelectedDay((d) => addDays(d, view === 'week' ? step * 7 : step));
  const goToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setSelectedDay(d);
  };

  const openBooking = (start?: Date, section?: number | null) => {
    const defaultStart = new Date(selectedDay);
    defaultStart.setHours(Math.max(8, sameDay(selectedDay, today) ? today.getHours() + 1 : 16), 0, 0, 0);
    setBooking({ start: localDateTime(start ?? defaultStart), section });
  };

  // Ha egy elemre kattintanak, a részletekhez ugyanazon esemény összes része kell
  const openSlot = (slot: OccupancySlot) => {
    const group = groupSlots(slots.filter((s) => s.source === slot.source && s.id === slot.id))[0];
    if (group) setDetail(group);
  };

  const label =
    view === 'week'
      ? `${weekStart.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })} – ${withDayDot(addDays(weekStart, 6).toLocaleDateString('hu-HU', {
          month: weekStart.getMonth() === addDays(weekStart, 6).getMonth() ? undefined : 'short',
          day: 'numeric',
        }))}`
      : capitalize(selectedDay.toLocaleDateString('hu-HU', { weekday: 'long', month: 'long', day: 'numeric' }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Fejléc */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pálya foglaltság</h1>
          <p className="mt-0.5 text-sm text-gray-500">Mikor, melyik pálya melyik része foglalt.</p>
        </div>
        {canBook && venue && (
          <button
            type="button"
            onClick={() => openBooking()}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-700"
          >
            <PlusIcon className="h-5 w-5" />
            <span className="hidden sm:inline">Foglalás</span>
          </button>
        )}
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      {venues.length === 0 ? (
        <div className="rounded-xl bg-white py-12 text-center shadow-sm">
          <ViewColumnsIcon className="mx-auto h-12 w-12 text-gray-300" />
          <p className="mt-2 text-gray-500">Nincs aktív pálya.</p>
        </div>
      ) : (
        <>
          {/* Pályaválasztó: kevés pályánál gombok, sok pályánál lenyíló */}
          {venues.length <= 6 ? (
            <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
              {venues.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setVenueId(v.id)}
                  aria-pressed={venueId === v.id}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    venueId === v.id ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {v.name}
                </button>
              ))}
            </div>
          ) : (
            <select
              value={venueId ?? ''}
              onChange={(e) => setVenueId(Number(e.target.value))}
              aria-label="Pálya"
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-primary-500 sm:max-w-xs sm:text-sm"
            >
              {venues.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          )}

          <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200">
            {/* Navigáció */}
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-3 py-2.5">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => move(-1)}
                  aria-label={view === 'week' ? 'Előző hét' : 'Előző nap'}
                  className="flex h-9 w-9 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => move(1)}
                  aria-label={view === 'week' ? 'Következő hét' : 'Következő nap'}
                  className="flex h-9 w-9 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
                <span className="ml-1 text-[15px] font-semibold text-gray-900">{label}</span>
              </div>
              <div className="flex items-center gap-2">
                {!(view === 'day' ? sameDay(selectedDay, today) : sameDay(weekStart, getMonday(today))) && (
                  <button
                    type="button"
                    onClick={goToday}
                    className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
                  >
                    Ma
                  </button>
                )}
                <div className="inline-flex rounded-lg bg-gray-100 p-0.5">
                  {(['day', 'week'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setView(m)}
                      aria-pressed={view === m}
                      className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                        view === m ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {m === 'day' ? 'Nap' : 'Hét'}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Hétsáv: napválasztó, pöttyökkel a foglalások számáról. Asztali heti
                nézetben a rács fejléce ugyanezt tudja, ott elhagyjuk. */}
            {(view === 'day' || isMobile) && (
              <div className="grid grid-cols-7 gap-1 border-b border-gray-100 px-2 py-2">
                {days.map((d, i) => {
                  const count = groupSlots(slotsOnDay(slots, d)).length;
                  const selected = view === 'day' && sameDay(d, selectedDay);
                  const isToday = sameDay(d, today);
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => {
                        setSelectedDay(d);
                        if (!isMobile) setView('day');
                      }}
                      className={`flex flex-col items-center rounded-xl py-1.5 transition-colors ${
                        selected ? 'bg-primary-600 text-white' : 'hover:bg-gray-50'
                      }`}
                    >
                      <span className={`text-[11px] font-semibold uppercase ${selected ? 'text-white/80' : isToday ? 'text-primary-600' : 'text-gray-500'}`}>
                        {DAY_SHORT[i]}
                      </span>
                      <span className={`text-base font-semibold tabular-nums ${selected ? '' : isToday ? 'text-primary-600' : 'text-gray-900'}`}>
                        {d.getDate()}
                      </span>
                      <span className="mt-0.5 flex h-1.5 gap-0.5">
                        {ready &&
                          Array.from({ length: Math.min(count, 3) }, (_, k) => (
                            <span key={k} className={`h-1.5 w-1.5 rounded-full ${selected ? 'bg-white' : 'bg-primary-400'}`} />
                          ))}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Tartalom */}
            <div>
              {!ready ? (
                <div className="space-y-2 p-3">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="h-14 animate-pulse rounded-xl bg-gray-100" />
                  ))}
                </div>
              ) : view === 'day' ? (
                isMobile ? (
                  <div className="p-3">
                    <DayAgenda
                      day={selectedDay}
                      groups={dayGroups}
                      sectionCount={sectionCount}
                      sectionNames={venue?.sectionNames}
                      canBook={canBook}
                      onBook={(start) => openBooking(start)}
                      onSelect={setDetail}
                    />
                  </div>
                ) : (
                  <DailyOccupancyGrid
                    slots={daySlots}
                    date={selectedDay}
                    venueSections={sectionCount}
                    sectionNames={venue?.sectionNames}
                    canBook={canBook}
                    onEmptyCellClick={(start, section) => openBooking(start, section)}
                    onSlotClick={openSlot}
                  />
                )
              ) : isMobile ? (
                <div className="space-y-4 p-3">
                  {days.map((d, i) => (
                    <div key={i}>
                      <p className={`mb-1.5 text-xs font-semibold uppercase tracking-wide ${sameDay(d, today) ? 'text-primary-600' : 'text-gray-500'}`}>
                        {capitalize(d.toLocaleDateString('hu-HU', { weekday: 'long', month: 'short', day: 'numeric' }))}
                      </p>
                      <DayAgenda
                        day={d}
                        groups={groupSlots(slotsOnDay(slots, d))}
                        sectionCount={sectionCount}
                        sectionNames={venue?.sectionNames}
                        canBook={canBook}
                        onBook={(start) => {
                          setSelectedDay(d);
                          openBooking(start);
                        }}
                        onSelect={setDetail}
                        compact
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <WeeklyOccupancyGrid
                  slots={slots}
                  weekStart={weekStart}
                  onDayClick={(d) => {
                    setSelectedDay(d);
                    setView('day');
                  }}
                  onSlotClick={setDetail}
                  sectionNames={venue?.sectionNames}
                  sectionCount={sectionCount}
                />
              )}
            </div>

            {/* Jelmagyarázat */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-gray-100 px-3 py-2.5 text-xs text-gray-500">
              {LEGEND.map((type) => (
                <span key={type} className="inline-flex items-center gap-1.5">
                  <span className={`h-2.5 w-2.5 rounded-full ${SLOT_STYLES[type].dot}`} />
                  {SLOT_TYPE_LABELS[type]}
                </span>
              ))}
              {canBook && !isMobile && view === 'day' && (
                <span className="ml-auto">Kattints egy szabad időpontra a foglaláshoz.</span>
              )}
            </div>
          </div>
        </>
      )}

      {/* Részletek: mikor, ki, melyik rész */}
      {detail && venue && (
        <Modal isOpen onClose={() => setDetail(null)} title={detail.slot.title}>
          <div className="space-y-4 pb-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${slotStyle(detail.slot.type).pill}`}>
                {SLOT_TYPE_LABELS[detail.slot.type] ?? detail.slot.type}
              </span>
              {detail.slot.ageGroup && <span className="text-sm text-gray-600">{detail.slot.ageGroup}</span>}
            </div>
            <div className="text-sm text-gray-700">
              <p className="font-medium text-gray-900">
                {capitalize(new Date(detail.slot.startTime).toLocaleDateString('hu-HU', { weekday: 'long', month: 'long', day: 'numeric' }))}
              </p>
              <p className="tabular-nums">
                {timeOf(detail.slot.startTime)} – {timeOf(detail.slot.endTime)}
              </p>
              {detail.slot.bookedByUserName && <p className="mt-1">Foglaló: {detail.slot.bookedByUserName}</p>}
            </div>
            {sectionCount > 1 && (
              <div>
                <p className="mb-1.5 text-sm font-medium text-gray-700">
                  {detail.sections === null ? 'Teljes pálya' : sectionsLabel(detail.sections, venue.sectionNames)}
                </p>
                <PitchGraphic sectionCount={sectionCount} highlighted={detail.sections} sectionNames={venue.sectionNames} label="" />
              </div>
            )}
            {detail.slot.eventId && (
              <Link
                to={`/events/${detail.slot.eventId}`}
                onClick={() => setDetail(null)}
                className="inline-flex text-sm font-medium text-primary-600 hover:underline"
              >
                Esemény megnyitása
              </Link>
            )}
          </div>
        </Modal>
      )}

      {venue && (
        <BookingModal
          isOpen={booking !== null}
          onClose={() => setBooking(null)}
          onSave={() => {
            setBooking(null);
            loadWeek();
          }}
          venueId={venue.id}
          venueSections={venue.sections}
          sectionNames={venue.sectionNames}
          prefilledStartTime={booking?.start}
          prefilledSection={booking?.section}
        />
      )}
    </div>
  );
};

export default VenueOccupancy;
